import {
  DateRangePicker,
  type DateRange,
  type DateRangePreset,
} from '@/components/common/DatePicker/DateRangePicker'
import { Card, CardHeader } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { BILL_LOCATION_LABELS, BILL_LOCATION_VALUES, type BillLocationValue } from '@/constants/bill-location'
import dayjs from '@/lib/dayjs'
import { useMemo } from 'react'

const BKK = 'Asia/Bangkok'

export type DashboardSummaryMode = 'today' | 'range' | 'year' | 'all'

function ymdToDate(ymd: string): Date {
  return dayjs.tz(ymd, 'YYYY-MM-DD', BKK).toDate()
}

function dateToYmd(d: Date): string {
  return dayjs(d).tz(BKK).format('YYYY-MM-DD')
}

function buildYearOptions(): { value: string; label: string }[] {
  const y = dayjs().tz(BKK).year()
  const out: { value: string; label: string }[] = []
  for (let i = 0; i <= 25; i++) {
    const ce = y - i
    out.push({ value: String(ce), label: `${ce} (พ.ศ. ${ce + 543})` })
  }
  return out
}

export function DashboardActions({
  summaryMode,
  onSummaryModeChange,
  dateFrom,
  dateTo,
  onDateRangeChange,
  summaryYear,
  onSummaryYearChange,
  reportLocation,
  onReportLocationChange,
}: {
  summaryMode: DashboardSummaryMode
  onSummaryModeChange: (m: DashboardSummaryMode) => void
  dateFrom: string
  dateTo: string
  onDateRangeChange: (from: string, to: string) => void
  summaryYear: number
  onSummaryYearChange: (y: number) => void
  reportLocation: '' | BillLocationValue
  onReportLocationChange: (value: '' | BillLocationValue) => void
}) {
  const rangeValue = useMemo((): DateRange | undefined => {
    if (!dateFrom && !dateTo) return undefined
    return {
      from: dateFrom ? ymdToDate(dateFrom) : undefined,
      to: dateTo ? ymdToDate(dateTo) : undefined,
    }
  }, [dateFrom, dateTo])

  const onRangePickerChange = (r: DateRange | undefined) => {
    if (!r?.from && !r?.to) {
      const n = dayjs().tz(BKK)
      onDateRangeChange(n.startOf('month').format('YYYY-MM-DD'), n.format('YYYY-MM-DD'))
      return
    }
    onDateRangeChange(r.from ? dateToYmd(r.from) : '', r.to ? dateToYmd(r.to) : '')
  }

  const rangePresets = useMemo((): DateRangePreset[] => {
    const n = dayjs().tz(BKK)
    return [
      {
        label: 'เดือนนี้',
        dateRange: { from: n.startOf('month').toDate(), to: n.endOf('day').toDate() },
      },
      {
        label: '30 วันล่าสุด',
        dateRange: { from: n.subtract(29, 'day').startOf('day').toDate(), to: n.endOf('day').toDate() },
      },
      {
        label: 'ปีนี้ (ถึงวันนี้)',
        dateRange: { from: n.startOf('year').toDate(), to: n.endOf('day').toDate() },
      },
    ]
  }, [])

  const yearOptions = useMemo(() => buildYearOptions(), [])

  return (
    <div className="flex flex-col mobile:flex-col md:flex-row w-full gap-6 px-0 mt-4">
      <Card className="flex-1 border-none shadow-sm rounded-[2rem] overflow-hidden">
        <CardHeader className="flex flex-col gap-4 py-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between w-full">
            <div className="flex flex-col gap-3 min-w-0 flex-1">
              <span className="text-sm font-medium text-slate-600">วันที่สรุป</span>
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:items-end">
                <div className="flex flex-col gap-2 min-w-[160px]">
                  <span className="text-xs text-slate-500">โหมด</span>
                  <Select
                    value={summaryMode}
                    onValueChange={(v) => onSummaryModeChange(v as DashboardSummaryMode)}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      <SelectItem value="today">วันนี้</SelectItem>
                      <SelectItem value="range">ช่วงวันที่</SelectItem>
                      <SelectItem value="year">ทั้งปี (ปีปฏิทิน)</SelectItem>
                      <SelectItem value="all">ทั้งหมด</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {summaryMode === 'today' ? (
                  <p className="text-sm text-slate-500 max-w-md pb-1">
                    สรุปเฉพาะบิลวันที่ปัจจุบัน (ตามเวลาไทย)
                  </p>
                ) : null}

                {summaryMode === 'range' ? (
                  <div className="flex flex-col gap-2 min-w-0 flex-1 sm:min-w-[280px]">
                    <span className="text-xs text-slate-500">เลือกช่วง</span>
                    <DateRangePicker
                      className="w-full min-w-0 rounded-xl border-slate-200"
                      presets={rangePresets}
                      value={rangeValue}
                      onChange={onRangePickerChange}
                    />
                  </div>
                ) : null}

                {summaryMode === 'year' ? (
                  <div className="flex flex-col gap-2 min-w-[200px]">
                    <span className="text-xs text-slate-500">ปี (ค.ศ.)</span>
                    <Select
                      value={String(summaryYear)}
                      onValueChange={(v) => onSummaryYearChange(Number.parseInt(v, 10))}
                    >
                      <SelectTrigger className="rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-xl max-h-[280px]">
                        {yearOptions.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}

                {summaryMode === 'all' ? (
                  <p className="text-sm text-slate-500 max-w-md pb-1">
                    สรุปจากบิลทุกวันที่ในระบบ (อาจใช้เวลาโหลดนานถ้ามีข้อมูลจำนวนมาก)
                  </p>
                ) : null}
              </div>
            </div>

            <div className="flex flex-col gap-2 min-w-[200px] shrink-0">
              <span className="text-sm font-medium text-slate-600">สถานที่</span>
              <Select
                value={reportLocation || 'all'}
                onValueChange={(v) =>
                  onReportLocationChange(v === 'all' ? '' : (v as BillLocationValue))
                }
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="ทุกสาขา" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="all">ทุกสาขา</SelectItem>
                  {BILL_LOCATION_VALUES.map((loc) => (
                    <SelectItem key={loc} value={loc}>
                      {BILL_LOCATION_LABELS[loc]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
      </Card>
    </div>
  )
}
